import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import * as fromApp from '../../store/app.reducer';
import * as CompanyActions from '../../components/company/store/company.actions';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, take } from 'rxjs/operators';
import { EmployeeModel } from '../../models/employee.model';
import { CustomerModel } from '../../models/customer.model';
import * as AuthActions from '../../auth/store/auth.actions';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SnackbarService } from '../snackbar/snackbar.service';

@Component({
    selector: 'app-create-company-modal',
    templateUrl: './create-company-modal.component.html',
    styleUrls: ['./create-company-modal.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatDialogModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        TranslateModule,
        MatTooltipModule
    ]
})
export class CreateCompanyModalComponent implements OnInit, OnDestroy {
    private subscriptions: Subscription[] = [];
    private searchCompleteSub?: Subscription;
    private businessNumberValueSub?: Subscription;
    public employee: EmployeeModel | null = null;
    companyForm: FormGroup;
    companyFormStep: number = 1;
    searchResults: CustomerModel[] = [];
    selectedCompany: CustomerModel | null = null;
    searchError: string | null = null;
    isSubmitting = false;
    /** True while the company search API is in progress (search button shows loading). */
    isSearchingBusiness = false;

    constructor(
        private dialogRef: MatDialogRef<CreateCompanyModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private store: Store<fromApp.AppState>,
        private actions$: Actions,
        private fb: FormBuilder,
        private snackbarService: SnackbarService
    ) {
        this.companyForm = this.fb.group({
            businessNumber: ['']
        });
        this.initForm();
    }

    private initForm(): void {
        this.companyFormStep = 1;
        this.companyForm.patchValue({ businessNumber: '' });
        this.selectedCompany = null;
        this.searchError = null;
        this.store.dispatch(new CompanyActions.ClearSearchResults());
    }

    ngOnInit(): void {
        // Subscribe to employee data
        this.subscriptions.push(
            this.store.select(state => state.auth.employee).subscribe(employee => {
                this.employee = employee;
            })
        );

        this.subscriptions.push(
            this.store.select(state => state.company).subscribe(companyState => {
                this.searchResults = companyState.searchResults;
                this.searchError = companyState.searchError;
            })
        );

        // Same as payment modal: API runs on input (debounced) or when user clicks search
        this.businessNumberValueSub = this.companyForm.get('businessNumber')?.valueChanges
            .pipe(
                debounceTime(400),
                distinctUntilChanged((prev, curr) => (prev?.toString?.().trim?.() ?? '') === (curr?.toString?.().trim?.() ?? ''))
            )
            .subscribe(() => {
                const raw = this.companyForm.get('businessNumber')?.value?.toString().trim() ?? '';
                if (raw.length > 9) {
                    this.searchBusiness();
                } else if (raw === '') {
                    this.store.dispatch(new CompanyActions.ClearSearchResults());
                }
            });

        // Auto-run search when business number is pre-filled (e.g. from data); debounced valueChanges will trigger the API
        const initial = this.data?.businessNumber?.toString().trim() ?? '';
        if (initial.length > 9) {
            this.companyForm.patchValue({ businessNumber: initial });
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.searchCompleteSub?.unsubscribe();
        this.businessNumberValueSub?.unsubscribe();
        this.store.dispatch(new CompanyActions.ClearSearchResults());
    }

    /** Search company by number (same API as payment modal organization search). */
    searchBusiness(): void {
        const query = this.companyForm.get('businessNumber')?.value?.toString().trim() ?? '';
        if (query.length > 9) {
            this.searchCompleteSub?.unsubscribe();
            this.isSearchingBusiness = true;
            this.store.dispatch(new CompanyActions.SearchCompany({ query }));
            this.searchCompleteSub = this.actions$.pipe(
                ofType(CompanyActions.SEARCH_COMPANY_SUCCESS, CompanyActions.SEARCH_COMPANY_FAILED),
                take(1)
            ).subscribe(() => {
                this.isSearchingBusiness = false;
            });
        } else {
            this.store.dispatch(new CompanyActions.ClearSearchResults());
        }
    }

    selectCompany(company: CustomerModel): void {
        this.selectedCompany = company;
        this.companyFormStep = 2;
    }

    searchAgain(): void {
        this.companyFormStep = 1;
        this.selectedCompany = null;
        this.companyForm.patchValue({ businessNumber: '' });
        this.store.dispatch(new CompanyActions.ClearSearchResults());
    }

    confirmCompanyDetails(): void {
        if (!this.selectedCompany) return;

        // Check if a company with the same idNumber already exists
        const existingCompany = this.employee?.customers?.find(
            customer => customer.idNumber === this.selectedCompany?.idNumber
        );

        if (existingCompany) {
            // Company already exists, just set it as active
            localStorage.setItem('customer', existingCompany.id!.toString());
            this.store.dispatch(new AuthActions.SwitchCompanyAccount({ customer: existingCompany }));
            this.snackbarService.showSuccess('company.company-already-exists-activated');
            
            // Reset form and close modal
            this.companyFormStep = 1;
            this.selectedCompany = null;
            this.companyForm.patchValue({ businessNumber: '' });
            this.store.dispatch(new CompanyActions.ClearSearchResults());
            this.dialogRef.close();
            return;
        }

        // Company doesn't exist, create it
        const formData = new FormData();
        formData.append('idNumber', this.selectedCompany.idNumber);
        formData.append('companyName', this.selectedCompany.companyName);
        formData.append('contactPerson', this.employee?.firstName + ' ' + this.employee?.lastName || this.selectedCompany?.contactPerson || '');
        formData.append('email', this.employee?.email || this.selectedCompany.email || '');
        formData.append('phone', this.employee?.phone || this.selectedCompany.phone || '');
        formData.append('address', this.selectedCompany.address ?? '');
        formData.append('city', this.selectedCompany.city ?? '');
        formData.append('biography', this.selectedCompany.biography ?? '');
        formData.append('customerWebsite', this.selectedCompany.customerWebsite ?? '');

        this.store.dispatch(new AuthActions.CreateCustomer(formData));
        
        // Reset form state after dispatching the action
        this.companyFormStep = 1;
        this.selectedCompany = null;
        this.companyForm.patchValue({ businessNumber: '' });
        this.store.dispatch(new CompanyActions.ClearSearchResults());
        this.dialogRef.close();
        }

    onCancel(): void {
        this.dialogRef.close();
    }
}